import { Shops } from '@infrab4a/connect';
import { getShopLabel } from 'src/app/connect-api/enums/ShopMap';

export type ShopCategoryImageField =
  | 'image'
  | 'brandBanner'
  | 'brandBannerMobile';

export type ShopCategoryImagesMap = Partial<
  Record<string, Partial<Record<ShopCategoryImageField, string | null>>>
>;

export const BRAND_CATEGORY_LOGO_SHOPS: string[] = [
  Shops.GLAMSHOP,
  Shops.MENSMARKET
];

export const BRAND_CATEGORY_PUBLISH_LOGO_ERROR =
  "Cadastre a logo principal da marca na Glam e na Men's Market antes de publicar.";

export function getMissingBrandCategoryLogos(
  images: ShopCategoryImagesMap | null | undefined,
  shops: string[] = BRAND_CATEGORY_LOGO_SHOPS
): string[] {
  const targetShops = (shops?.length ? shops : BRAND_CATEGORY_LOGO_SHOPS).filter(
    Boolean
  );
  return targetShops.filter((shop) => !images?.[shop]?.image);
}

export function hasBrandCategoryLogos(
  images: ShopCategoryImagesMap | null | undefined,
  shops: string[] = BRAND_CATEGORY_LOGO_SHOPS
): boolean {
  return getMissingBrandCategoryLogos(images, shops).length === 0;
}

export function getBrandCategoryPublishLogoError(
  images?: ShopCategoryImagesMap | null,
  shops?: string[]
): string {
  return getCategoryPublishImageError(images, shops, true);
}

export function getMissingImagesForPublish(
  images: ShopCategoryImagesMap | null | undefined,
  shops: string[] | undefined,
  isBrandCategory: boolean
): string[] {
  if (isBrandCategory) {
    return getMissingBrandCategoryLogos(images, shops);
  }

  const targetShops = (shops?.length ? shops : []).filter(Boolean);
  return getMissingShopCategoryImageShops(images, targetShops, 'brandBanner');
}

export function getCategoryPublishImageError(
  images: ShopCategoryImagesMap | null | undefined,
  shops: string[] | undefined,
  isBrandCategory: boolean
): string {
  const missing = getMissingImagesForPublish(images, shops, isBrandCategory);
  if (!missing.length) {
    return isBrandCategory
      ? BRAND_CATEGORY_PUBLISH_LOGO_ERROR
      : 'Cadastre o banner nas lojas selecionadas antes de publicar.';
  }

  const names = formatShopNames(missing);
  if (isBrandCategory) {
    return `Cadastre a logo principal da marca em ${names} antes de publicar.`;
  }

  const bannerLabel = missing.length === 1 ? 'o banner' : 'os banners';
  return `Cadastre ${bannerLabel} em ${names} antes de publicar.`;
}

export function getMissingShopCategoryImageShops(
  images: ShopCategoryImagesMap | null | undefined,
  shops: string[],
  field: ShopCategoryImageField
): string[] {
  return shops.filter((shop) => !images?.[shop]?.[field]);
}

export function getShopCategoryMissingImagesWarning(
  images: ShopCategoryImagesMap | null | undefined,
  shops: string[],
  isBrandCategory: boolean
): string | null {
  const targetShops = (shops?.length ? shops : []).filter(Boolean);
  if (!targetShops.length) {
    return null;
  }

  const missingLogos = isBrandCategory
    ? getMissingShopCategoryImageShops(images, targetShops, 'image')
    : [];
  const missingBanners = isBrandCategory
    ? []
    : getMissingShopCategoryImageShops(images, targetShops, 'brandBanner');

  if (!missingLogos.length && !missingBanners.length) {
    return null;
  }

  const parts: string[] = [];
  if (missingLogos.length) {
    parts.push(
      `${missingLogos.length === 1 ? 'logo' : 'logos'} (${formatShopNames(
        missingLogos
      )})`
    );
  }
  if (missingBanners.length) {
    parts.push(
      `${missingBanners.length === 1 ? 'banner' : 'banners'} (${formatShopNames(
        missingBanners
      )})`
    );
  }

  const verb =
    missingLogos.length + missingBanners.length === 1 ? 'falta' : 'faltam';
  return `Atenção: ${verb} ${parts.join(' e ')} nesta categoria.`;
}

function formatShopNames(shops: string[]): string {
  const labels = shops.map((shop) => getShopLabel(shop));
  if (labels.length === 1) {
    return labels[0];
  }

  return `${labels.slice(0, -1).join(', ')} e ${labels[labels.length - 1]}`;
}

export function areShopCategoryImagesUnchanged(
  saved: ShopCategoryImagesMap | null | undefined,
  current: ShopCategoryImagesMap | null | undefined,
  shops: string[]
): boolean {
  if (!saved || !current) {
    return false;
  }

  const shopsToCheck = shops.length ? shops : Object.keys(saved);
  return shopsToCheck.every((shop) => {
    return (
      (saved[shop]?.image || null) === (current[shop]?.image || null) &&
      (saved[shop]?.brandBanner || null) ===
        (current[shop]?.brandBanner || null) &&
      (saved[shop]?.brandBannerMobile || null) ===
        (current[shop]?.brandBannerMobile || null)
    );
  });
}

export function shouldValidateCategoryImagesOnPublish(params: {
  published: boolean;
  wasPublished?: boolean;
  isBrandCategory?: boolean;
  wasBrandCategory?: boolean;
}): boolean {
  if (!params.published) {
    return false;
  }

  const wasSameKind = !!params.isBrandCategory === !!params.wasBrandCategory;
  return !(params.wasPublished && wasSameKind);
}

export function shouldValidateBrandCategoryLogosOnSave(params: {
  isBrandCategory: boolean;
  published: boolean;
  wasBrandCategory?: boolean;
  wasPublished?: boolean;
}): boolean {
  return shouldValidateCategoryImagesOnPublish(params);
}

export function canSaveCategoryPublish(params: {
  isBrandCategory: boolean;
  published: boolean;
  wasBrandCategory?: boolean;
  wasPublished?: boolean;
  images?: ShopCategoryImagesMap | null;
  shops?: string[];
}): boolean {
  if (!shouldValidateCategoryImagesOnPublish(params)) {
    return true;
  }

  return (
    getMissingImagesForPublish(
      params.images,
      params.shops,
      params.isBrandCategory
    ).length === 0
  );
}

export function canSaveBrandCategoryPublish(params: {
  isBrandCategory: boolean;
  published: boolean;
  wasBrandCategory?: boolean;
  wasPublished?: boolean;
  images?: ShopCategoryImagesMap | null;
  shops?: string[];
}): boolean {
  return canSaveCategoryPublish(params);
}

export function getRequiredShopCategoryImageFields(
  isBrandCategory: boolean
): ShopCategoryImageField[] {
  if (isBrandCategory) {
    return ['image'];
  }

  return ['brandBanner'];
}

export function isShopCategoryImageFieldRequired(
  isBrandCategory: boolean,
  field: ShopCategoryImageField
): boolean {
  return getRequiredShopCategoryImageFields(isBrandCategory).includes(field);
}

export function getMissingRequiredShopCategoryImages(
  shops: string[],
  images: ShopCategoryImagesMap,
  isBrandCategory: boolean
): Array<{ shop: string; field: ShopCategoryImageField }> {
  const requiredFields = getRequiredShopCategoryImageFields(isBrandCategory);
  const missing: Array<{ shop: string; field: ShopCategoryImageField }> = [];

  shops.forEach((shop) => {
    requiredFields.forEach((field) => {
      if (!images[shop]?.[field]) {
        missing.push({ shop, field });
      }
    });
  });

  return missing;
}

export function copyBannerToImageFields(
  images: ShopCategoryImagesMap,
  isBrandCategory: boolean
): ShopCategoryImagesMap {
  if (!images) {
    return images;
  }

  return Object.keys(images).reduce<ShopCategoryImagesMap>((next, shop) => {
    const shopImages = images[shop];
    if (!shopImages) {
      next[shop] = shopImages;
      return next;
    }

    const banner = shopImages.brandBanner || null;
    next[shop] = {
      ...shopImages,
      brandBanner: banner,
      brandBannerMobile: banner,
      ...(isBrandCategory ? {} : { image: banner })
    };
    return next;
  }, {});
}

export function getNonBrandCategoryMainImage(
  images: ShopCategoryImagesMap,
  shops: string[]
): string | null {
  for (const shop of shops) {
    if (images[shop]?.brandBanner) {
      return images[shop].brandBanner;
    }
  }

  return null;
}
